/** Optional menu formatting. Missing properties preserve the renderer's existing CSS. */
export const menuRoles = [
  "bar",
  "panel",
  "parent",
  "child",
  "grandchild",
  "group",
  "heading",
  "description",
  "button",
  "dropdown",
  "mega",
] as const;
export type MenuRole = (typeof menuRoles)[number];
export interface MenuTextStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  fontStyle?: "normal" | "italic";
  textTransform?: "none" | "uppercase" | "lowercase";
  lineHeight?: number;
  letterSpacing?: number;
  textAlign?: "left" | "center" | "right";
  color?: string;
  backgroundColor?: string;
  hoverColor?: string;
  hoverBackgroundColor?: string;
  activeColor?: string;
  activeBackgroundColor?: string;
  expandedColor?: string;
  expandedBackgroundColor?: string;
  textDecoration?: "none" | "underline";
  hoverEffect?: "chevron" | "underline" | "background" | "none";
  paddingX?: number;
  paddingY?: number;
  indent?: number;
  gap?: number;
  separatorColor?: string;
}
export interface ResponsiveMenuStyle {
  base?: MenuTextStyle;
  desktop?: MenuTextStyle;
  mobile?: MenuTextStyle;
}
export interface MenuFormatting {
  common?: ResponsiveMenuStyle;
  roles?: Partial<Record<MenuRole, ResponsiveMenuStyle>>;
  items?: Record<string, ResponsiveMenuStyle>;
}
export interface MenuFormattingSite {
  theme?: unknown;
  menuTypography?: MenuFormatting;
  menuFormatting?: Record<string, MenuFormatting>;
  layout?: { header?: Record<string, any> };
  headers?: {
    id: string;
    menuId?: string;
    menuSource?: string;
    config: Record<string, any>;
  }[];
  pages?: { id: string; layout?: { header?: Record<string, any> } }[];
}
export const roleParents: Partial<Record<MenuRole, MenuRole>> = {
  parent: "panel",
  group: "parent",
  child: "panel",
  grandchild: "child",
  heading: "group",
  description: "child",
  dropdown: "child",
  mega: "child",
};
export function roleChain(role: MenuRole): MenuRole[] {
  const parent = roleParents[role];
  return [...(parent ? roleChain(parent) : []), role];
}
const fonts = [
  "body",
  "heading",
  "Arya",
  "Cormorant Garamond",
  "Inter",
  "Lato",
  "Merriweather",
  "Montserrat",
  "Plus Jakarta Sans",
  "Prata",
  "Quicksand",
  "Roboto",
  "Roboto Slab",
  "Titillium Web",
];
// The shape Core's HomepageMenuFormattingContract accepts: a palette colour or a global role, never an object.
const colourReference = /^var\(--re-(p-)?[a-z0-9-]{1,40}\)$/;
// What menuFormattingsInDark draws for an own colour code with a dark version, also behind a Synlighed; never stored.
const ownColourVersion =
  /^(var\(--re-o-([0-9a-f]{6}), #\2\)|color-mix\(in srgb, var\(--re-o-([0-9a-f]{6}), #\3\) [\d.]+%, transparent\))$/;
/** Whether the menu draws `value` as a colour: a code, and with `references` a palette, role or own colour's variable. */
export function menuColour(value: unknown, references = false): boolean {
  return (
    typeof value === "string" &&
    (/^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(value) ||
      (references &&
        (colourReference.test(value) || ownColourVersion.test(value))))
  );
}
/** `references` admits palette and role references, which only a look-system website defines. */
export function safeMenuStyle(
  value: unknown,
  references = false,
): MenuTextStyle {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result: Record<string, unknown> = {};
  const ranges: Record<string, [number, number]> = {
    fontSize: [8, 120],
    lineHeight: [0.8, 4],
    letterSpacing: [-2, 20],
    paddingX: [0, 120],
    paddingY: [0, 120],
    indent: [0, 120],
    gap: [0, 120],
  };
  const choices: Record<string, string[]> = {
    fontFamily: fonts,
    fontStyle: ["normal", "italic"],
    textTransform: ["none", "uppercase", "lowercase"],
    textAlign: ["left", "center", "right"],
    textDecoration: ["none", "underline"],
    hoverEffect: ["chevron", "underline", "background", "none"],
  };
  const colours = [
    "color",
    "backgroundColor",
    "hoverColor",
    "hoverBackgroundColor",
    "activeColor",
    "activeBackgroundColor",
    "expandedColor",
    "expandedBackgroundColor",
    "separatorColor",
  ];
  for (const [key, v] of Object.entries(value)) {
    const range = ranges[key];
    if (
      (range &&
        typeof v === "number" &&
        Number.isFinite(v) &&
        v >= range[0] &&
        v <= range[1]) ||
      (key === "fontWeight" && [300, 400, 600, 700].includes(v as number)) ||
      (typeof v === "string" &&
        (choices[key]?.includes(v) ||
          (colours.includes(key) && menuColour(v, references))))
    )
      result[key] = v;
  }
  return result as MenuTextStyle;
}
export function resolveMenuStyle(
  website: MenuFormatting | undefined,
  menu: MenuFormatting | undefined,
  role: MenuRole,
  itemId?: string,
  device: "mobile" | "desktop" = "desktop",
  surface?: "dropdown" | "mega",
  references = false,
): MenuTextStyle {
  const read = (value?: ResponsiveMenuStyle) => ({
    ...safeMenuStyle(value?.base, references),
    ...safeMenuStyle(value?.[device], references),
  });
  const result: MenuTextStyle = Object.assign(
    {},
    read(website?.common),
    ...roleChain(role).map((key) => read(website?.roles?.[key])),
    read(surface ? website?.roles?.[surface] : undefined),
    read(menu?.common),
    ...roleChain(role).map((key) => read(menu?.roles?.[key])),
    read(surface ? menu?.roles?.[surface] : undefined),
    read(itemId ? menu?.items?.[itemId] : undefined),
  );
  if (
    result.fontFamily &&
    fonts.includes(result.fontFamily) &&
    !["body", "heading"].includes(result.fontFamily) &&
    result.fontWeight !== undefined
  ) {
    const available =
      result.fontFamily === "Prata"
        ? [400]
        : result.fontFamily === "Arya"
          ? [400, 700]
          : result.fontFamily === "Roboto Slab"
            ? [300, 400, 600, 700]
            : [300, 400, 700];
    if (!available.includes(result.fontWeight)) result.fontWeight = 400;
  }
  return result;
}
/** No writes and no menu materialisation: implicit menus continue following the Pages list. */
export function formattingMenuId(
  site: MenuFormattingSite,
  config: Record<string, any>,
) {
  if (typeof config._formattingMenuId === "string")
    return config._formattingMenuId;
  if (typeof config.menuId === "string") return config.menuId;
  const page = site.pages?.find((page) => page.layout?.header === config);
  if (page && (config.navigation || config.classic?.navigation))
    return `legacy-page:${page.id}`;
  const definition =
    site.headers?.find((header) => header.id === config.id) ??
    site.headers?.find((header) => header.id === site.layout?.header?.activeId);
  if (definition?.menuId ?? definition?.config.menuId)
    return (definition?.menuId ?? definition?.config.menuId) as string;
  if (definition?.menuSource === "own") return `legacy-header:${definition.id}`;
  return "standard";
}
